<template>
  <div>
    <v-card flat>
      <v-card-title class="text-h5 font-weight-bold">
        {{ $t('Landing Page') }}
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-expansion-panels v-model="openPanel">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  {{ $t('Theme Colors') }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col class="shrink" style="min-width: 220px">
                      <v-text-field
                        v-model="formData.theme.main_color"
                        hide-details
                        class="ma-0 pa-0"
                        :placeholder="$t('Main color')"
                        solo
                      >
                        <template #append>
                          <div
                            :style="swatchStyle(formData.theme.main_color)"
                            @click="menuMain = !menuMain"
                          />
                        </template>
                      </v-text-field>
                      <v-menu
                        v-model="menuMain"
                        :close-on-content-click="false"
                        offset-y
                      >
                        <v-color-picker
                          v-model="formData.theme.main_color"
                          mode="hex"
                          @input="updateForm"
                        />
                      </v-menu>
                    </v-col>

                    <v-col class="shrink" style="min-width: 220px">
                      <v-text-field
                        v-model="formData.theme.secondary_color"
                        hide-details
                        class="ma-0 pa-0"
                        :placeholder="$t('Secondary color')"
                        solo
                      >
                        <template #append>
                          <div
                            :style="swatchStyle(formData.theme.secondary_color)"
                            @click="menuSecondary = !menuSecondary"
                          />
                        </template>
                      </v-text-field>
                      <v-menu
                        v-model="menuSecondary"
                        :close-on-content-click="false"
                        offset-y
                      >
                        <v-color-picker
                          v-model="formData.theme.secondary_color"
                          mode="hex"
                          @input="updateForm"
                        />
                      </v-menu>
                    </v-col>

                    <v-col class="shrink" style="min-width: 220px">
                      <v-text-field
                        v-model="formData.theme.actions_color"
                        hide-details
                        class="ma-0 pa-0"
                        :placeholder="$t('Actions color')"
                        solo
                      >
                        <template #append>
                          <div
                            :style="swatchStyle(formData.theme.actions_color)"
                            @click="menuActions = !menuActions"
                          />
                        </template>
                      </v-text-field>
                      <v-menu
                        v-model="menuActions"
                        :close-on-content-click="false"
                        offset-y
                      >
                        <v-color-picker
                          v-model="formData.theme.actions_color"
                          mode="hex"
                          @input="updateForm"
                        />
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-header>
                  {{ $t('Header Content') }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field
                    v-model="formData.sections.header.title"
                    dense
                    outlined
                    :rules="[required]"
                    :label="$t('Title')"
                    @input="updateForm"
                  />

                  <v-text-field
                    v-model="formData.sections.header.sub_title"
                    dense
                    outlined
                    :label="$t('Subtitle')"
                    @input="updateForm"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-header>
                  {{ $t('Images') }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12">
                      <v-input :value="formData.bg_image" :rules="[required]" />
                      <gallery
                        :key="formData.bg_image"
                        button-text="Background image"
                        folder="booking-engine"
                        :compress="false"
                        :cols="6"
                        :thumbnails="true"
                        readonly
                        :images="formData.bg_image ? [formData.bg_image] : []"
                        :on-change="afterBgUpload"
                        :uploadable="true"
                      />
                    </v-col>

                    <v-col cols="12">
                      <gallery
                        :key="formData.logo"
                        button-text="Site Logo"
                        folder="booking-engine"
                        :compress="false"
                        :cols="6"
                        readonly
                        :thumbnails="true"
                        :images="formData.logo ? [formData.logo] : []"
                        :on-change="afterLogoUpload"
                        :uploadable="true"
                      />
                    </v-col>

                    <v-col cols="12">
                      <gallery
                        :key="formData.favicon"
                        button-text="Favicon"
                        folder="booking-engine"
                        :compress="false"
                        :cols="6"
                        readonly
                        :thumbnails="true"
                        :images="formData.favicon ? [formData.favicon] : []"
                        :on-change="afterFaviconUpload"
                        :uploadable="true"
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-header>
                  {{ $t('Destinations') }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-select
                    v-model="formData.destination_cities"
                    :label="$t('Cities')"
                    outlined
                    item-text="name"
                    return-object
                    dense
                    multiple
                    :hide-details="true"
                    :items="destinationCities"
                    @change="updateForm"
                  />

                  <v-row class="mt-4">
                    <v-col
                      v-for="(city, key) in formData.destination_cities"
                      :key="key"
                      cols="12"
                    >
                      <gallery
                        :button-text="city.name"
                        folder="booking-engine"
                        :compress="false"
                        :cols="6"
                        :thumbnails="true"
                        :images="city.image ? [city.image] : []"
                        :on-change="
                          imgs => afterCityUpload(imgs, city.name, key)
                        "
                        :uploadable="true"
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>

          <v-col cols="12" md="6">
            <preview-frame
              :url="previewUrl"
              :needs-refresh="needsRefresh"
              @refresh="refreshPreview"
              @load="onFrameLoad"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import FormRulesMixin from 'components/mixins/form-rules-mixin'
import Gallery from 'components/common/gallery.vue'
import PreviewFrame from '../components/preview-frame.vue'
import axios from 'axios'

export default {
  name: 'LandingPage',

  components: {
    Gallery,
    PreviewFrame,
  },

  mixins: [FormRulesMixin],

  props: {
    bookingEngineSetting: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      menuMain: false,
      menuSecondary: false,
      menuActions: false,
      needsRefresh: false,
      isFrameLoading: true,
      openPanel: 0,
      formData: {
        name: this.bookingEngineSetting.name,
        sections: {
          header: {
            title: this.bookingEngineSetting.sections?.header?.title || '',
            sub_title:
              this.bookingEngineSetting.sections?.header?.sub_title || '',
          },
        },
        theme: {
          main_color: this.bookingEngineSetting.theme?.main_color || '#44a2a2',
          secondary_color:
            this.bookingEngineSetting.theme?.secondary_color || '#dfe0f2',
          actions_color:
            this.bookingEngineSetting.theme?.actions_color || '#464673',
        },
        bg_image: this.bookingEngineSetting.bg_image || null,
        logo: this.bookingEngineSetting.logo || null,
        favicon: this.bookingEngineSetting.favicon || null,
        destination_cities: this.bookingEngineSetting.destination_cities || [],
      },
    }
  },

  computed: {
    ...mapGetters(['listingCities']),

    destinationCities() {
      return this.listingCities.map(city => ({ name: city, image: null }))
    },

    previewUrl() {
      // For development, use localhost. In production, use bookingsboom.com domain
      return process.env.NODE_ENV === 'development'
        ? 'http://localhost:8081/'
        : `https://${this.bookingEngineSetting.name}.bookingsboom.com`
    },

    showError() {
      return !this.$refs.form?.validate()
    },
  },

  methods: {
    swatchStyle(color) {
      return {
        backgroundColor: color,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderRadius: this.menuMain ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },

    updateForm() {
      const updateData = {
        theme: {
          main_color: this.formData.theme.main_color,
          secondary_color: this.formData.theme.secondary_color,
          actions_color: this.formData.theme.actions_color,
        },
        sections: { ...this.formData.sections },
        bg_image: this.formData.bg_image,
        logo: this.formData.logo,
        favicon: this.formData.favicon,
        destination_cities: this.formData.destination_cities,
      }

      this.$emit('update:setting', updateData)
      this.needsRefresh = true
    },

    afterBgUpload(imgs) {
      this.formData.bg_image = imgs[0]
      this.updateForm()
    },

    afterLogoUpload(imgs) {
      this.formData.logo = imgs[0]
      this.updateForm()
    },

    afterFaviconUpload(imgs) {
      this.formData.favicon = imgs[0]
      this.updateForm()
    },

    afterCityUpload(imgs, name, key) {
      this.formData.destination_cities[key] = {
        name,
        image: imgs[0],
      }
      this.$forceUpdate()
      this.updateForm()
    },

    validate() {
      return this.$refs.form.validate()
    },

    async refreshPreview() {
      if (!this.$refs.form?.validate()) return

      try {
        this.isFrameLoading = true

        // Save changes to backend first
        await axios.patch(
          `/api/booking-engine-settings/${this.bookingEngineSetting.id}`,
          {
            name: this.bookingEngineSetting.name,
            sections: this.formData.sections,
            theme: {
              main_color: this.formData.theme.main_color,
              secondary_color: this.formData.theme.secondary_color,
              actions_color: this.formData.theme.actions_color,
            },
            bg_image: this.formData.bg_image,
            logo: this.formData.logo,
            favicon: this.formData.favicon,
            destination_cities: this.formData.destination_cities,
          }
        )

        this.needsRefresh = false
      } catch (error) {
        console.error('Failed to save settings:', error)
        this.$store.dispatch('snackbar/showSnackbar', {
          message: this.$t('Failed to save settings'),
          color: 'error',
        })
      }
    },

    onFrameLoad() {
      this.isFrameLoading = false
    },
  },
}
</script>

<style scoped>
.v-text-field ::v-deep .v-input__append-inner {
  margin-top: 4px !important;
}

.preview-container {
  position: relative;
}

.preview-frame-container {
  position: relative;
}

iframe {
  border: none;
}
</style>
